import React, { useState } from 'react';
import { Alert, Button, Descriptions, Image, Input, Modal, Radio, Space, Table, Tabs, Tag, message, notification } from 'antd';
import { useReceivedShipments, useShipmentMutations } from '../../hooks/useStudioOps';

function isCmrImage(doc) {
    const mime = String(doc?.mimeType || '');
    if (mime.startsWith('image/')) return true;
    return /\.(jpe?g|png|gif|webp|bmp)$/i.test(String(doc?.name || doc?.key || ''));
}

function CmrSheets({ docs }) {
    const items = docs || [];
    if (!items.length) return <div style={{ marginTop: 8, color: '#888' }}>No CMR sheet</div>;
    return (
        <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>CMR sheets</div>
            <Image.PreviewGroup>
                <Space wrap>
                    {items.map((doc, i) =>
                        isCmrImage(doc) && doc.url ? (
                            <Image
                                key={doc.key || i}
                                src={doc.url}
                                alt={doc.name || 'CMR'}
                                width={140}
                                style={{ objectFit: 'contain', border: '1px solid #eee', borderRadius: 4 }}
                            />
                        ) : (
                            <a key={doc.key || i} href={doc.url} target="_blank" rel="noreferrer">
                                {doc.name || 'CMR document'}
                            </a>
                        )
                    )}
                </Space>
            </Image.PreviewGroup>
        </div>
    );
}

function apiError(err, fallback) {
    return err?.response?.data?.message || err?.message || fallback;
}

const STATUS = {
    1000: 'Pending Approval',
    1100: 'Rejected',
    2000: 'Approved',
    2100: 'In Progress',
    3000: 'Inbound Completed',
    3100: 'Closed',
};

const UNIT_STATUS = {
    1000: 'Registered',
    2000: 'Ready for PII',
    2100: 'PII in progress',
    3000: 'Closed',
    3100: 'Closed short',
    3200: 'Damaged hold',
};

const PAGE_SIZE = 20;
const CLOSABLE = new Set([2000, 2100]);
const CLOSED = new Set([3000, 3100]);

function unitStatusTag(status) {
    if (status === 3000) return <Tag color="green">{UNIT_STATUS[status]}</Tag>;
    if (status === 3100) return <Tag color="orange">{UNIT_STATUS[status]}</Tag>;
    if (status === 3200) return <Tag color="red">{UNIT_STATUS[status]}</Tag>;
    if (CLOSABLE.has(status)) return <Tag color="blue">{UNIT_STATUS[status]}</Tag>;
    return <Tag>{UNIT_STATUS[status] || status}</Tag>;
}

export default function ReceivedShipments() {
    const [tab, setTab] = useState('open');
    const [pageNum, setPageNum] = useState(1);
    const [errorBanner, setErrorBanner] = useState(null);
    const [closingUnit, setClosingUnit] = useState(null);
    const [closeOutcome, setCloseOutcome] = useState('complete');
    const [closeReason, setCloseReason] = useState('');
    const [api, contextHolder] = notification.useNotification();
    const params = tab === 'approval' ? { status: 1000, pageNum, pageSize: PAGE_SIZE } : { open: 'true', pageNum, pageSize: PAGE_SIZE };
    const { data, isFetching } = useReceivedShipments(params);
    const mutations = useShipmentMutations();

    const showFailure = (title, err, fallback) => {
        const text = apiError(err, fallback);
        setErrorBanner({ title, text });
        api.error({ message: title, description: text, duration: 0 });
    };

    const approve = async id => {
        try {
            await mutations.approve.mutateAsync(id);
            setErrorBanner(null);
            message.success('Approved');
        } catch (err) {
            showFailure('Approval failed', err, 'Could not approve shipment');
        }
    };

    const openCloseModal = unit => {
        setClosingUnit(unit);
        setCloseOutcome('complete');
        setCloseReason('');
    };

    const confirmClose = async () => {
        if (closeOutcome === 'short' && !closeReason.trim()) {
            message.error('Reason required to close short');
            return;
        }
        try {
            await mutations.closeUnit.mutateAsync({
                id: closingUnit._id,
                outcome: closeOutcome,
                reason: closeReason.trim() || undefined,
            });
            message.success(`Closed ${closingUnit.unitLabel}`);
            setClosingUnit(null);
        } catch (err) {
            showFailure('Close unit failed', err, 'Could not close unit');
        }
    };

    const columns = [
        { title: 'Code', dataIndex: 'shipmentCode' },
        { title: 'Client', dataIndex: 'companyCode' },
        { title: 'Carrier', dataIndex: 'carrier' },
        { title: 'Status', dataIndex: 'status', render: s => <Tag>{STATUS[s] || s}</Tag> },
        {
            title: 'CMR',
            width: 120,
            render: (_, row) => {
                const docs = row.cmrDocuments || [];
                const first = docs.find(doc => isCmrImage(doc) && doc.url);
                if (!docs.length) return '—';
                if (!first) {
                    return docs[0].url ? (
                        <a href={docs[0].url} target="_blank" rel="noreferrer">
                            {docs[0].name || 'CMR'}
                        </a>
                    ) : (
                        docs[0].name || 'CMR'
                    );
                }
                return (
                    <Image
                        src={first.url}
                        alt={first.name || 'CMR'}
                        width={72}
                        height={56}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                    />
                );
            },
        },
        {
            title: 'Units',
            render: (_, row) => {
                const units = row.units || [];
                const closed = units.filter(u => CLOSED.has(u.status)).length;
                return `${units.length} (${closed} closed)`;
            },
        },
        { title: 'Inbounded', render: (_, r) => (r.units || []).reduce((n, u) => n + (u.inboundedItemCount || 0), 0) },
        {
            title: 'Action',
            width: 180,
            render: (_, row) =>
                row.status === 1000 ? (
                    <Space>
                        <Button size="small" type="primary" loading={mutations.approve.isLoading} onClick={() => approve(row._id)}>
                            Approve
                        </Button>
                        <Button
                            size="small"
                            danger
                            onClick={() => {
                                let reason = '';
                                Modal.confirm({
                                    title: 'Reject shipment',
                                    content: <Input onChange={e => (reason = e.target.value)} placeholder="Reason" />,
                                    onOk: () => {
                                        if (!reason) {
                                            message.error('Reason required');
                                            return Promise.reject();
                                        }
                                        return mutations.reject.mutateAsync({ id: row._id, reason }).catch(err => {
                                            showFailure('Reject failed', err, 'Could not reject shipment');
                                            return Promise.reject(err);
                                        });
                                    },
                                });
                            }}
                        >
                            Reject
                        </Button>
                    </Space>
                ) : (
                    <span style={{ color: '#888' }}>Expand for units</span>
                ),
        },
    ];

    const unitColumns = [
        { title: 'Label', dataIndex: 'unitLabel' },
        { title: 'Type', dataIndex: 'unitType' },
        { title: 'Expected', dataIndex: 'expectedItemCount', width: 90 },
        { title: 'Inbounded SKUs', dataIndex: 'inboundedItemCount', width: 130 },
        { title: 'Status', dataIndex: 'status', render: unitStatusTag, width: 140 },
        {
            title: '',
            key: 'actions',
            width: 90,
            render: (_, unit) =>
                CLOSABLE.has(unit.status) ? (
                    <Button size="small" onClick={() => openCloseModal(unit)}>
                        Close
                    </Button>
                ) : null,
        },
    ];

    const expected = closingUnit?.expectedItemCount;
    const inbounded = closingUnit?.inboundedItemCount || 0;
    const shortVsExpected = expected != null && inbounded < expected;

    return (
        <div style={{ padding: 16 }}>
            {contextHolder}
            {errorBanner && (
                <Alert
                    type="error"
                    showIcon
                    closable
                    message={errorBanner.title}
                    description={errorBanner.text}
                    onClose={() => setErrorBanner(null)}
                    style={{ marginBottom: 12 }}
                />
            )}
            <Tabs
                activeKey={tab}
                onChange={key => {
                    setTab(key);
                    setPageNum(1);
                }}
items={[{ key: 'open', label: 'Open Shipments' }, { key: 'approval', label: 'Approval Queue' }]} />
            <Table
                loading={isFetching}
                rowKey="_id"
                dataSource={data?.data?.data || []}
                columns={columns}
                expandable={{
                    expandedRowRender: row => (
                        <div>
                            <CmrSheets docs={row.cmrDocuments} />
                            <Table
                                size="small"
                                rowKey="_id"
                                dataSource={row.units || []}
                                columns={unitColumns}
                                pagination={(row.units || []).length > 10 ? { pageSize: 10, size: 'small' } : false}
                                scroll={{ y: (row.units || []).length > 8 ? 360 : undefined }}
                            />
                        </div>
                    ),
                }}
                pagination={{
                    current: pageNum,
                    pageSize: PAGE_SIZE,
                    total: data?.data?.totalCount,
                    showSizeChanger: false,
                    onChange: setPageNum,
                }}
            />
            <Modal
                open={!!closingUnit}
                title={closingUnit ? `Close ${closingUnit.unitType || 'unit'} ${closingUnit.unitLabel}` : 'Close unit'}
                okText="Confirm close"
                confirmLoading={mutations.closeUnit.isLoading}
                onCancel={() => setClosingUnit(null)}
                onOk={confirmClose}
            >
                {closingUnit && (
                    <>
                        <Descriptions size="small" column={1} bordered>
                            <Descriptions.Item label="Label">{closingUnit.unitLabel}</Descriptions.Item>
                            <Descriptions.Item label="Type">{closingUnit.unitType}</Descriptions.Item>
                            <Descriptions.Item label="Condition">{closingUnit.condition || 'OK'}</Descriptions.Item>
                            <Descriptions.Item label="Expected items">{expected != null ? expected : '—'}</Descriptions.Item>
                            <Descriptions.Item label="SKUs inbounded">{inbounded}</Descriptions.Item>
                        </Descriptions>
                        {shortVsExpected && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginTop: 12 }}
                                message={`${inbounded} inbounded, ${expected} expected. Close short if this pallet/box is done.`}
                            />
                        )}
                        <div style={{ marginTop: 16 }}>
                            <Radio.Group value={closeOutcome} onChange={e => setCloseOutcome(e.target.value)}>
                                <Radio value="complete">Close complete</Radio>
                                <Radio value="short">Close short</Radio>
                            </Radio.Group>
                        </div>
                        {closeOutcome === 'short' && (
                            <Input.TextArea
                                className="mt-2"
                                rows={2}
                                placeholder="Shortage reason"
                                value={closeReason}
                                onChange={e => setCloseReason(e.target.value)}
                            />
                        )}
                    </>
                )}
            </Modal>
        </div>
    );
}
